import {
  SocialPlatform,
  SocialPlatformId,
  socialPlatforms,
} from '@app/modules/social-export/domain/platforms';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

export type PlatformSelectProps = {
  /** Current selected platform id(s). */
  value?: SocialPlatformId | '' | SocialPlatformId[];
  /** Called when the user selects a different option. */
  onChange?: (value: SocialPlatformId | '' | SocialPlatformId[]) => void;
  /** When true, multiple platforms can be selected. */
  multiple?: boolean;
  /** When true, an empty option is shown so the user can clear the selection. Only for single select. */
  allowEmpty?: boolean;
  /** Restrict options to these platform ids. If not set, all enabled platforms are shown. */
  platformIds?: SocialPlatformId[];
  /** Label for the select. */
  label?: string;
  /** Id for the select and label; used for accessibility. Defaults to "platform-select". */
  id?: string;
  /** When true, the select takes full width of its container. */
  fullWidth?: boolean;
  disabled?: boolean;
  /** Optional blur handler (e.g. for react-hook-form). */
  onBlur?: () => void;
  /** Optional ref for the select input (e.g. for react-hook-form). */
  inputRef?: React.Ref<unknown>;
  /** MUI sx prop for the FormControl (e.g. minWidth for filter bar). */
  sx?: SxProps<Theme>;
};

/**
 * Raw MUI Select for choosing one or more social platforms. List items and the selected value (chip)
 * show the platform icon. Use with value/onChange.
 * For form context, use PlatformSelectField instead.
 */
export function PlatformSelect({
  value,
  onChange,
  multiple = false,
  allowEmpty = false,
  platformIds,
  label,
  id = 'platform-select',
  fullWidth = true,
  disabled = false,
  onBlur,
  inputRef,
  sx,
}: PlatformSelectProps) {
  const { $t } = useIntl();

  const platforms = useMemo(() => {
    const all = socialPlatforms.all();
    if (platformIds?.length) {
      return all.filter((p) => platformIds.includes(p.id));
    }
    return all;
  }, [platformIds]);

  const handleChange = (event: SelectChangeEvent<SocialPlatformId | '' | SocialPlatformId[]>) => {
    const v = event.target.value;
    if (multiple) {
      const ids = (Array.isArray(v) ? v : [v].filter(Boolean)) as SocialPlatformId[];
      onChange?.(ids);
    } else {
      onChange?.(v as SocialPlatformId | '');
    }
  };

  const currentValue = value ?? (multiple ? [] : '');

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled} size="small" sx={sx}>
      {label && <InputLabel id={`${id}-label`}>{label}</InputLabel>}
      <Select<SocialPlatformId | '' | SocialPlatformId[]>
        labelId={label ? `${id}-label` : undefined}
        id={id}
        multiple={multiple}
        value={currentValue}
        label={label}
        onChange={handleChange}
        onBlur={onBlur}
        inputRef={inputRef}
        displayEmpty={allowEmpty && !multiple}
        renderValue={(selected) => {
          if (multiple) {
            const selectedIds = (Array.isArray(selected) ? selected : []) as SocialPlatformId[];
            if (selectedIds.length === 0) return null;
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedIds
                  .map((id) => platforms.find((p) => p.id === id))
                  .filter((p): p is SocialPlatform => p != null)
                  .map((platform) => (
                    <Chip
                      size="small"
                      key={platform.id}
                      label={platform.Icon({ colored: true, sx: { fontSize: 16 } })}
                    />
                  ))}
              </Box>
            );
          }

          // Single selection
          if (!selected)
            return allowEmpty
              ? $t({ id: 'socialExport.select.none', defaultMessage: 'Ninguna' })
              : null;

          const platform = platforms.find((p) => p.id === selected);
          if (!platform) return selected;

          return (
            <Chip
              size="small"
              icon={platform.Icon({ colored: true, sx: { fontSize: 18 } }) as React.ReactElement}
              label={platform.name}
              sx={{ '& .MuiChip-icon': { ml: 0.5, mr: -0.5 } }}
            />
          );
        }}
      >
        {allowEmpty && !multiple && (
          <MenuItem value="">
            <ListItemText
              primary={$t({ id: 'socialExport.select.none', defaultMessage: 'Ninguna' })}
            />
          </MenuItem>
        )}
        {platforms.map((platform: SocialPlatform) => (
          <MenuItem key={platform.id} value={platform.id}>
            <ListItemIcon sx={{ minWidth: 40 }}>{platform.Icon({ colored: true })}</ListItemIcon>
            <ListItemText primary={platform.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
